import { Card, Table, Tag } from 'antd'
import { useEffect, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { gold, red } from '@ant-design/colors'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons'
import { useActiveBatchQuery, useBatchHistoryInvalidator, useBatchHistoryQuery, useBatchStatusQuery } from '../../../domain/invoicing/invoicingQueries'
import { BatchHistoryItem } from '../../../domain/invoicing/invoicingApi'
import { isBatchProcessing, batchStatusConfig } from '../BatchCreate/batchInvoicingModel'
import { hasBatchScope, scopeDateLabel, scopeDivisionsLabel, scopeWorkAreaStatusesLabel } from '../BatchCreate/batchScopeModel'
import BatchProgressBanner from '../BatchCreate/BatchProgressBanner'
import VSpace from '../../common/VSpace'
import { getBatchTypeCopy } from '../batchTypeCopy'
import { BatchMeta } from './batchMeta'

type Props = {
  drawerBatchId: string | null
  onSelectBatch: (batchId: string, meta: BatchMeta) => void
}

const BatchHistoryView = ({ drawerBatchId, onSelectBatch }: Props) => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const { data, isLoading } = useBatchHistoryQuery({ page, pageSize })

  const activeBatchQuery = useActiveBatchQuery()
  const batchStatus = activeBatchQuery.data?.status
  const isBatchRunning = isBatchProcessing(batchStatus)
  const activeBatchId = activeBatchQuery.data?.batchId ?? null

  const { data: batchStatusData } = useBatchStatusQuery(activeBatchId)
  const batchHistoryInvalidator = useBatchHistoryInvalidator()
  const prevStatus = useRef(batchStatusData?.status)

  useEffect(() => {
    const prev = prevStatus.current
    const current = batchStatusData?.status
    prevStatus.current = current
    if (prev && prev !== current && (current === 'processing' || current === 'completed' || current === 'failed')) {
      batchHistoryInvalidator.invalidate()
    }
  }, [batchStatusData?.status, batchHistoryInvalidator])

  const showBanner = isBatchRunning && activeBatchId && drawerBatchId !== activeBatchId

  return (
    <VSpace>
      {showBanner && activeBatchQuery.data && (
        <BatchProgressBanner
          batchId={activeBatchId}
          onDismiss={() => {}}
          batchType={activeBatchQuery.data.batchType}
        />
      )}

      <Card>
        <Table<BatchHistoryItem>
          size="small"
          loading={isLoading}
          dataSource={data?.items ?? []}
          rowKey={x => x.batchId}
          columns={[
            {
              title: 'Type',
              width: 120,
              render: (_, x) => getBatchTypeCopy(x.batchType).label
            },
            {
              title: 'Date',
              render: (_, x) => dayjs(x.createdAt).format('MMM D, YYYY h:mm A')
            },
            {
              title: 'Scope',
              render: (_, x) => {
                if (!hasBatchScope(x)) {
                  return '--'
                }
                return <div>
                  <div>{scopeDateLabel(x)}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{scopeDivisionsLabel(x)}</div>
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>{scopeWorkAreaStatusesLabel(x)}</div>
                </div>
              }
            },
            {
              title: 'Failed',
              align: 'right',
              render: (_, x) => x.failedJobs > 0
                ? <span style={{ color: red[7] }}>{x.failedJobs}</span>
                : x.failedJobs
            },
            {
              title: 'Skipped',
              align: 'right',
              render: (_, x) => (x.skippedJobs ?? 0) > 0
                ? <span style={{ color: gold[7] }}>{x.skippedJobs}</span>
                : x.skippedJobs ?? 0
            },
            {
              title: 'Succeeded',
              align: 'right',
              render: (_, x) => x.completedJobs
            },
            {
              title: 'Total',
              align: 'right',
              render: (_, x) => x.totalJobs
            },
            {
              title: 'Created By',
              render: (_, x) => x.requestedByUserName
            },
            {
              title: 'Status',
              render: (_, x) => <div>
                <StatusTag status={x.status} />
                {(x.status === 'completed' || x.status === 'failed') && x.completedAt && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                    {dayjs(x.completedAt).format('MMM D, YYYY h:mm A')}
                  </div>
                )}
              </div>
            },
            {
              width: 40,
              render: () => <FontAwesomeIcon icon={faChevronRight} />
            }
          ]}
          onRow={(record) => ({
            onClick: () => onSelectBatch(record.batchId, {
              createdAt: record.createdAt,
              startDate: record.startDate || undefined,
              cutOffDate: record.cutOffDate || undefined,
              divisionGroups: record.divisionGroups || undefined,
              workAreaStatuses: record.workAreaStatuses || undefined,
              requestedByUserName: record.requestedByUserName,
              batchType: record.batchType,
            }),
            style: { cursor: 'pointer' }
          })}
          pagination={{
            current: page,
            pageSize,
            total: data?.totalCount,
            hideOnSinglePage: true,
            onChange: (p, ps) => { setPage(p); setPageSize(ps) }
          }}
          locale={{ emptyText: 'No batch history found.' }}
        />
      </Card>
    </VSpace>
  )
}

const StatusTag = ({ status }: { status: BatchHistoryItem['status'] }) => {
  const config = batchStatusConfig[status]
  return <Tag color={config.color}>{config.label}</Tag>
}

export default BatchHistoryView
